import { parseAslBelgisi } from './parser';
import { PosEvent, type SaleContext } from '@core/bus/events';
import type { Plugin, PluginContext, HandlerResult } from '@core/bus/types';
import { debugLog } from '@core/utils/log'

export interface UzCrptPluginConfig {
  enabled: boolean;
}

/**
 * Плагин национальной маркировки «Asl Belgisi» (Узбекистан).
 *
 * Подписывается на PRE_SALE и валидирует коды DataMatrix маркированных позиций
 * (молочка, напитки, табак) парсером GS1. Если у маркируемой позиции код марки
 * невалиден — накладывает veto на продажу.
 *
 * Оформлен как наш контракт Plugin (id/country/init) для регистрации в реестре,
 * с сохранением публичного класса UzCrptPlugin для совместимости.
 */
export class UzCrptPlugin {
  private config: UzCrptPluginConfig;

  constructor(config: UzCrptPluginConfig = { enabled: true }) {
    this.config = config;
  }

  /** Подписка на шину (адаптировано под реальный контракт EventBus/PRE_SALE). */
  public register(bus: PluginContext['bus']): void {
    if (!this.config.enabled) return;
    debugLog('[UzCrptPlugin] Asl Belgisi (Uzbekistan Marking) plugin registered');

    bus.on(PosEvent.PRE_SALE, (sale: SaleContext): HandlerResult => {
      for (const item of sale.items) {
        if (!item.requiresMarking) continue;
        if (!item.datamatrix) {
          return {
            ok: false,
            vetoReason: `Asl Belgisi: не отсканирована марка для «${item.name}»`,
          };
        }
        const mark = parseAslBelgisi(item.datamatrix);
        if (!mark.isValid) {
          return {
            ok: false,
            vetoReason: `Asl Belgisi: некорректный код маркировки для «${item.name}»`,
          };
        }
        debugLog(
          `[UzCrptPlugin] Valid Asl Belgisi DataMatrix: GTIN ${mark.gtin}, serial ${mark.serial}`
        );
      }
      return { ok: true };
    });
  }
}

/**
 * Фабрика плагина в формате контракта Plugin (country: 'UZ') — для подключения
 * в bootstrap через PluginRegistry при сборке узбекского набора плагинов.
 */
export function createUzCrptPlugin(config: UzCrptPluginConfig = { enabled: true }): Plugin {
  const impl = new UzCrptPlugin(config);
  return {
    id: 'uz-crpt',
    country: 'UZ',
    init(ctx: PluginContext): void {
      impl.register(ctx.bus);
    },
  };
}

/** Готовый экземпляр плагина для регистрации. */
export const uzCrptPlugin: Plugin = createUzCrptPlugin();
